import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase/supabase';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);
const ACTIVE_TENANT_KEY = 'saleswork_active_tenant_id';
const isProduction = import.meta.env.PROD === true;

export const useTenant = () => {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
};

const normalizeTenant = tenant => ({
  ...tenant,
  logo: tenant.logo || '🏢',
  plan: tenant.plan || 'internal',
  planId: tenant.plan || 'internal'
});

const TenantProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [activeTenantId, setActiveTenantIdState] = useState(null);
  const [error, setError] = useState('');

  const loadTenants = async userId => {
    if (!userId) {
      setTenants([]);
      setMemberships([]);
      setActiveTenantIdState(null);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const { data: membershipRows, error: membershipError } = await supabase
      .from('platform_tenant_memberships')
      .select('id, tenant_id, user_id, role, status')
      .eq('user_id', userId)
      .eq('status', 'active');

    if (membershipError) {
      console.error('[TenantContext] Kunne ikke laste medlemskap.', membershipError);
      setError(`Klarte ikke hente organisasjonsmedlemskap: ${membershipError.message}`);
      setTenants([]);
      setMemberships([]);
      setActiveTenantIdState(null);
      setLoading(false);
      return;
    }

    const activeMemberships = membershipRows || [];
    setMemberships(activeMemberships);

    if (activeMemberships.length === 0) {
      setTenants([]);
      setActiveTenantIdState(null);
      setError('Brukeren er ikke koblet til en organisasjon. Logg ut og inn igjen, eller kontakt administrator.');
      setLoading(false);
      return;
    }

    const tenantIds = activeMemberships.map(item => item.tenant_id);
    const { data: organizationRows, error: organizationError } = await supabase
      .from('platform_organizations')
      .select('id, name, slug, org_number, contact_email, status, plan')
      .in('id', tenantIds)
      .eq('status', 'active');

    if (organizationError) {
      console.error('[TenantContext] Kunne ikke laste organisasjoner.', organizationError);
      setError(`Klarte ikke hente organisasjon: ${organizationError.message}`);
      setTenants([]);
      setMemberships([]);
      setActiveTenantIdState(null);
      setLoading(false);
      return;
    }

    const availableTenants = (organizationRows || []).map(normalizeTenant);
    const storedTenantId = typeof localStorage !== 'undefined'
      ? localStorage.getItem(ACTIVE_TENANT_KEY)
      : null;
    const salesworkTenant = availableTenants.find(
      tenant => tenant.name?.trim().toLowerCase() === 'saleswork crm'
    );
    const validStoredTenantId = availableTenants.some(
      tenant => tenant.id === storedTenantId
    );

    const nextTenantId = availableTenants.length === 1
      ? availableTenants[0].id
      : validStoredTenantId
        ? storedTenantId
        : salesworkTenant?.id || availableTenants[0]?.id || null;

    setTenants(availableTenants);
    setActiveTenantIdState(nextTenantId);

    if (nextTenantId && typeof localStorage !== 'undefined') {
      localStorage.setItem(ACTIVE_TENANT_KEY, nextTenantId);
    }

    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const loadSession = async () => {
      const {
        data: { user },
        error: userError
      } = await supabase.auth.getUser();

      if (!mounted) return;

      if (userError) {
        console.error('[TenantContext] Kunne ikke hente innlogget bruker.', userError);
        setError(`Klarte ikke hente bruker: ${userError.message}`);
        setTenants([]);
        setMemberships([]);
        setActiveTenantIdState(null);
        setLoading(false);
        return;
      }

      await loadTenants(user?.id || null);
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (mounted) {
          await loadTenants(session?.user?.id || null);
        }
      }
    );

    loadSession();

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const setActiveTenant = tenantId => {
    if (!tenants.some(tenant => tenant.id === tenantId)) return;

    setActiveTenantIdState(tenantId);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    }
  };

  const activeTenant = useMemo(
    () => tenants.find(tenant => tenant.id === activeTenantId) || null,
    [tenants, activeTenantId]
  );

  const activeMembership = useMemo(
    () => memberships.find(membership => membership.tenant_id === activeTenantId) || null,
    [memberships, activeTenantId]
  );

  const activeRole = activeMembership?.role || 'Sales';

  const value = {
    loading,
    error,
    tenants,
    memberships,
    activeTenant,
    activeTenantId,
    activeMembership,
    activeRole,
    setActiveTenant,
    reloadTenants: loadTenants
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const TenantGate = ({ children }) => {
  const { loading, error, activeTenantId } = useTenant();
  const { isAuthenticated, loading: authLoading, previewMode } = useAuth();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <p className="text-sm text-slate-600">Laster organisasjon...</p>
      </div>
    );
  }

  if (!isProduction && previewMode) {
    return children;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Organisasjon mangler</h1>
          <p className="mt-3 text-sm text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!activeTenantId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md rounded-2xl border border-amber-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-bold text-slate-900">Organisasjon mangler</h1>
          <p className="mt-3 text-sm text-slate-600">
            Brukeren er ikke koblet til en organisasjon. Kontakt administrator.
          </p>
        </div>
      </div>
    );
  }

  return children;
};

export default TenantProvider;